import PropTypes from "prop-types";
import React, { useCallback } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLanguage } from "redux-i18n";

import { LANGUAGE_SELECT_OPTIONS } from "lib/constants";
import DSFilterButton from "common/DS/FilterButton";
import { GlobeIcon } from "common/DS/Icons";

const Portal = ({ mountElementId, children }) => {
    const el = document.getElementById(mountElementId);

    if (!el) return <div>{children}</div>;

    return createPortal(children, el);
};

Portal.propTypes = {
    mountElementId: PropTypes.string.isRequired,
};

const PortalMemo = React.memo(Portal);

const LanguageSelector = ({ onChange, selectedLanguage, mountElementId, supportedLanguages }) => {
    const dispatch = useDispatch();
    const i18nLang = useSelector((state) => state.i18nState.lang);

    const getLanguageText = (needle) =>
        supportedLanguages.find(({ value }) => value === needle)?.text ?? "";

    const handleChangeLanguage = useCallback((value) => {
        dispatch(setLanguage(value));
        if (onChange) {
            onChange({ value });
        }
    }, []);

    const button = (
        <DSFilterButton
            defaultEmptyOption={getLanguageText(i18nLang)}
            options={supportedLanguages}
            handleChange={handleChangeLanguage}
            debounceMs={50}
            label={
                <>
                    <GlobeIcon /> {getLanguageText(selectedLanguage || i18nLang)}
                </>
            }
        />
    );

    if (mountElementId) {
        return <PortalMemo mountElementId={mountElementId}>{button}</PortalMemo>;
    }

    return button;
};

LanguageSelector.propTypes = {
    onChange: PropTypes.func,
    selectedLanguage: PropTypes.string,
    mountElementId: PropTypes.string,
    supportedLanguages: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string,
            value: PropTypes.string,
        }),
    ),
};

LanguageSelector.defaultProps = {
    onChange: null,
    selectedLanguage: null,
    mountElementId: "language-selector",
    supportedLanguages: LANGUAGE_SELECT_OPTIONS,
};

export default React.memo(LanguageSelector);
